/**
 * Price Elasticity of Demand — Calculation Utilities
 * 
 * Single source of truth for:
 *  - LOESS smoothing (local linear regression with tricube + recency weighting)
 *  - Log-log OLS elasticity
 *  - Revenue-curve optimum price
 *  - Monthly MoM elasticity
 *  - Data transformation from API → elasticity format
 */

// ─────────────────────────────────────────────
// LOESS ENGINE (single implementation)
// ─────────────────────────────────────────────

/**
 * Generic LOESS smoother.
 * Fits local linear regressions with tricube kernel + optional recency weighting.
 *
 * @param {Array<{x: number, y: number, date?: string}>} points
 * @param {Object} opts
 * @param {number} opts.span       - Fraction of points in local window (default 0.6)
 * @param {number} opts.gridSize   - Number of prediction grid points (default auto)
 * @param {boolean} opts.useRecency - Weight recent months more (default true)
 * @param {number} opts.clipLo     - Revenue percentile clip lower (default 0.05)
 * @param {number} opts.clipHi     - Revenue percentile clip upper (default 0.95)
 * @returns {{ curve: Array<{x:number, y:number}>, optimum: {x:number, y:number}|null }}
 */
export function loessSmooth(points, opts = {}) {
  const {
    span = 0.6,
    gridSize: requestedGrid,
    useRecency = true,
    clipLo = 0.05,
    clipHi = 0.95,
  } = opts;

  if (!points || points.length < 3) {
    return { curve: [], optimum: null };
  }

  // Sort by x
  const sorted = [...points].sort((a, b) => a.x - b.x);
  const xs = sorted.map(p => p.x);
  const minX = xs[0];
  const maxX = xs[xs.length - 1];

  // Clip y values to reduce outlier impact
  const ySorted = sorted.map(p => p.y).slice().sort((a, b) => a - b);
  const yLo = quantile(ySorted, clipLo);
  const yHi = quantile(ySorted, clipHi);
  const clippedY = sorted.map(p => Math.max(yLo, Math.min(yHi, p.y)));

  // Recency weights (newer months weigh more)
  let recencyWeights = null;
  if (useRecency && sorted[0].date) {
    const dateObjs = sorted.map(p => new Date(p.date));
    const latest = new Date(Math.max(...dateObjs.map(d => d.getTime())));
    recencyWeights = dateObjs.map(d => {
      const monthsAgo = Math.max(0, monthDiff(latest, d));
      return Math.pow(0.7, monthsAgo);
    });
  }

  // Grid
  const gridSize = requestedGrid || Math.min(60, Math.max(20, Math.ceil(sorted.length * 2.5)));
  const grid = Array.from({ length: gridSize }, (_, i) =>
    minX + ((maxX - minX) * i) / Math.max(1, gridSize - 1)
  );

  // Tricube kernel
  const tricube = (u) => (u >= 1 ? 0 : Math.pow(1 - Math.pow(u, 3), 3));

  const predictAt = (x0) => {
    const n = xs.length;
    const distances = xs.map(x => Math.abs(x - x0)).sort((a, b) => a - b);
    const k = Math.max(2, Math.floor(span * n));
    const bandwidth = Math.max(1e-9, distances[k - 1] || distances[distances.length - 1] || 1);

    const w = xs.map((x, idx) => {
      const base = tricube(Math.abs(x - x0) / bandwidth);
      const rec = recencyWeights ? recencyWeights[idx] : 1;
      return base * rec;
    });

    // Weighted linear regression: y = a + b*x
    let Sw = 0, Swx = 0, Swy = 0, Swxx = 0, Swxy = 0;
    for (let i = 0; i < n; i++) {
      const wi = w[i];
      const xi = xs[i];
      const yi = clippedY[i];
      Sw += wi;
      Swx += wi * xi;
      Swy += wi * yi;
      Swxx += wi * xi * xi;
      Swxy += wi * xi * yi;
    }

    const denom = Sw * Swxx - Swx * Swx;
    if (denom === 0 || Sw === 0) return Sw === 0 ? 0 : Swy / Sw;

    const a = (Swy * Swxx - Swx * Swxy) / denom;
    const b = (Sw * Swxy - Swx * Swy) / denom;
    return a + b * x0;
  };

  let best = { x: xs[Math.floor(xs.length / 2)], y: -Infinity };
  const curve = grid.map(x => {
    const y = predictAt(x);
    if (Number.isFinite(y) && y > best.y) best = { x, y };
    return { x, y };
  });

  return { curve, optimum: best, predictAt };
}

// ─────────────────────────────────────────────
// ELASTICITY CALCULATIONS
// ─────────────────────────────────────────────

/**
 * Long-term elasticity via log-log OLS:
 *   ln(Q) = α + β·ln(P)  →  β is elasticity
 */
export function computeLogLogElasticity(historicalPrices) {
  if (!Array.isArray(historicalPrices) || historicalPrices.length < 2) return 0;

  const valid = historicalPrices.filter(p => p && Number(p.price) > 0 && Number(p.demand) > 0);
  if (valid.length < 2) return 0;

  const xs = valid.map(p => Math.log(Number(p.price)));
  const ys = valid.map(p => Math.log(Number(p.demand)));
  const n = xs.length;
  const meanX = xs.reduce((s, v) => s + v, 0) / n;
  const meanY = ys.reduce((s, v) => s + v, 0) / n;

  let num = 0, den = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    num += dx * (ys[i] - meanY);
    den += dx * dx;
  }

  if (den === 0) return 0;
  const beta = num / den;
  return Number.isFinite(beta) ? beta : 0;
}

/**
 * Revenue-curve based optimum price using LOESS on (Price, Revenue=P*Q).
 * Returns { optimumPrice, optimumRevenue, expectedDemand }
 */
export function computeRevenueOptimalPrice(historicalPrices, currentPrice) {
  const pts = (historicalPrices || [])
    .filter(p => Number(p.price) > 0 && Number(p.demand) >= 0)
    .map(p => ({
      x: Number(p.price),
      y: Number(p.price) * Number(p.demand),
      date: p.date,
    }));

  if (pts.length < 3) {
    // Fallback: simple elasticity-based
    const e = computeLogLogElasticity(historicalPrices || []);
    const absE = Math.abs(e);
    const fallbackPrice = absE <= 1
      ? currentPrice * 1.1
      : Math.max(currentPrice * 0.8, Math.min(currentPrice * 1.2, currentPrice * (absE / (absE - 1))));
    const lastDemand = historicalPrices?.[historicalPrices.length - 1]?.demand || 0;
    const currentRev = lastDemand * (currentPrice || 0);
    return { optimumPrice: fallbackPrice, optimumRevenue: currentRev, expectedDemand: lastDemand };
  }

  const { predictAt } = loessSmooth(pts);

  // Search within observed price range
  const prices = pts.map(p => p.x).sort((a, b) => a - b);
  const minP = prices[0];
  const maxP = prices[prices.length - 1];

  const gridSize = Math.min(60, Math.max(20, Math.ceil(pts.length * 2.5)));
  const grid = Array.from({ length: gridSize }, (_, i) =>
    minP + ((maxP - minP) * i) / Math.max(1, gridSize - 1)
  );

  let bestP = prices[Math.floor(prices.length / 2)];
  let bestR = -Infinity;
  for (const x of grid) {
    const yhat = predictAt(x);
    if (Number.isFinite(yhat) && yhat > bestR) {
      bestR = yhat;
      bestP = x;
    }
  }

  // Dynamic guardrails
  const revSorted = pts.map(p => p.y).sort((a, b) => a - b);
  const p75 = revSorted[Math.floor(0.75 * (revSorted.length - 1))] || 0;
  const lastRev = pts[pts.length - 1]?.y || 0;
  const guardBand = lastRev >= p75 ? 0.10 : 0.20;

  const lower = currentPrice ? Math.max(minP, currentPrice * (1 - guardBand)) : minP;
  const upper = currentPrice ? Math.min(maxP, currentPrice * (1 + guardBand)) : maxP;
  const pStar = Math.max(lower, Math.min(upper, bestP));
  const rStar = predictAt(pStar);
  const qStar = pStar > 0 ? rStar / pStar : 0;

  // Prefer current price if it already achieves equal/higher revenue
  const currentRevVal = (historicalPrices?.[historicalPrices.length - 1]?.demand || 0) *
    (historicalPrices?.[historicalPrices.length - 1]?.price || currentPrice || 0);
  if (currentRevVal >= rStar) {
    return { optimumPrice: currentPrice || pStar, optimumRevenue: currentRevVal, expectedDemand: currentRevVal / (currentPrice || pStar) };
  }

  return { optimumPrice: pStar, optimumRevenue: rStar, expectedDemand: qStar };
}

/**
 * Build LOESS curve data for visualization.
 * @param {'revenue'|'demand'} target
 */
export function buildLoessCurve(historicalPrices, target = 'revenue') {
  const pts = (historicalPrices || [])
    .filter(p => Number(p.price) > 0 && Number(p.demand) >= 0)
    .map(p => ({
      x: Number(p.price),
      y: target === 'revenue' ? Number(p.price) * Number(p.demand) : Number(p.demand),
      date: p.date,
    }));

  if (pts.length < 3) {
    return {
      curve: [],
      points: pts.map(p => ({ price: p.x, [target]: p.y })),
      optimum: null,
    };
  }

  const { curve, optimum } = loessSmooth(pts);

  return {
    curve: curve.map(c => ({ price: c.x, [target]: c.y })),
    points: (historicalPrices || [])
      .filter(p => Number(p.price) > 0 && Number(p.demand) >= 0)
      .map(p => ({
        price: Number(p.price),
        demand: Number(p.demand),
        revenue: Number(p.price) * Number(p.demand),
        date: p.date,
      })),
    optimum: optimum ? { price: optimum.x, [target]: optimum.y } : null,
  };
}

/**
 * Monthly MoM elasticity between consecutive months.
 * Excludes current (incomplete) month.
 */
export function computeMonthlyElasticities(historicalPrices) {
  if (!Array.isArray(historicalPrices) || historicalPrices.length < 2) return [];

  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const filtered = historicalPrices.filter(p => {
    const d = new Date(p?.date);
    if (isNaN(d.getTime())) return false;
    const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return mk !== currentKey;
  });

  if (filtered.length < 2) return [];
  const sorted = [...filtered].sort((a, b) => new Date(a.date) - new Date(b.date));

  const rows = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    const priceChange = prev.price ? (cur.price - prev.price) / prev.price : null;
    const demandChange = prev.demand ? (cur.demand - prev.demand) / prev.demand : null;
    const revenue = (Number(cur.price) || 0) * (Number(cur.demand) || 0);

    let elasticity = null;
    if (priceChange === 0) elasticity = 0;
    else if (priceChange != null && demandChange != null) elasticity = demandChange / priceChange;

    rows.push({ period: cur.date, price: cur.price, demand: cur.demand, revenue, priceChange, demandChange, elasticity });
  }
  return rows;
}

// ─────────────────────────────────────────────
// DATA TRANSFORMATION
// ─────────────────────────────────────────────

/**
 * Transform monthly API rows into elasticity-ready product objects.
 */
export function transformMonthlyDataForElasticity(apiData) {
  if (!apiData || !Array.isArray(apiData) || apiData.length === 0) return [];

  // Group by product
  const productGroups = {};

  apiData.forEach(row => {
    const itemId = row.itemId || row.item_id;
    const mcTitle = nonEmpty(row.mcTitle);
    const iName = nonEmpty(row.itemName) || nonEmpty(row.item_name);
    const productName = mcTitle || iName;
    const productKey = itemId || productName;
    const year = Number(row.year);
    const month = Number(row.month);
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;

    if (!productKey || !year || !month) return;

    if (!productGroups[productKey]) {
      productGroups[productKey] = {
        id: itemId || productKey,
        name: productName || String(productKey),
        category: row.itemCategory || row.item_category || 'General',
        brand: row.itemBrand || row.item_brand || 'Unknown',
        monthlyData: {},
        imageLink: row.mcImageLink || row.mc_image_link || null,
        mcCategory: row.mcGoogleProductCategory || row.mc_google_product_category || null,
      };
    }

    const pg = productGroups[productKey];
    if (!pg.imageLink && (row.mcImageLink || row.mc_image_link)) {
      pg.imageLink = row.mcImageLink || row.mc_image_link;
    }
    if (!pg.mcCategory && (row.mcGoogleProductCategory || row.mc_google_product_category)) {
      pg.mcCategory = row.mcGoogleProductCategory || row.mc_google_product_category;
    }

    if (!pg.monthlyData[monthKey]) {
      pg.monthlyData[monthKey] = { totalRevenue: 0, totalPurchases: 0, totalViews: 0 };
    }

    const md = pg.monthlyData[monthKey];
    md.totalRevenue += parseFloat((row.totalRevenue ?? row.total_revenue) || 0);
    md.totalPurchases += parseInt((row.totalItemsPurchased ?? row.total_items_purchased) || 0);
    md.totalViews += parseInt((row.totalItemsViewed ?? row.total_items_viewed) || 0);
  });

  // Filter products with ≥2 meaningful months
  let meaningful = Object.values(productGroups)
    .map(product => {
      const months = Object.keys(product.monthlyData).filter(mk => {
        const md = product.monthlyData[mk];
        return md.totalRevenue > 0 && md.totalPurchases > 0;
      });
      return { ...product, meaningfulMonths: months };
    })
    .filter(p => p.meaningfulMonths.length >= 2);

  // Fallback: loosen to ≥1 month
  if (meaningful.length === 0) {
    meaningful = Object.values(productGroups)
      .map(product => {
        const months = Object.keys(product.monthlyData).filter(mk => {
          const md = product.monthlyData[mk];
          return md.totalRevenue > 0 || md.totalPurchases > 0;
        });
        return { ...product, meaningfulMonths: months };
      })
      .filter(p => p.meaningfulMonths.length >= 1);
  }

  // Sort by total revenue descending
  meaningful.sort((a, b) => {
    const aRev = a.meaningfulMonths.reduce((s, m) => s + a.monthlyData[m].totalRevenue, 0);
    const bRev = b.meaningfulMonths.reduce((s, m) => s + b.monthlyData[m].totalRevenue, 0);
    return bRev - aRev;
  });

  // Convert to elasticity format
  return meaningful.map((product, index) => {
    const historicalPrices = product.meaningfulMonths.sort().map(mk => {
      const md = product.monthlyData[mk];
      const avgPrice = md.totalPurchases > 0 ? md.totalRevenue / md.totalPurchases : 0;
      return { date: `${mk}-01`, price: Math.max(1, Math.round(avgPrice * 100) / 100), demand: md.totalPurchases };
    });

    const latestMonth = historicalPrices[historicalPrices.length - 1];
    const elasticity = computeLogLogElasticity(historicalPrices);
    const revenueOpt = computeRevenueOptimalPrice(historicalPrices, latestMonth.price);
    const currentRevenue = latestMonth.demand * latestMonth.price;
    const potentialGrowth = currentRevenue > 0 ? ((revenueOpt.optimumRevenue / currentRevenue) - 1) * 100 : 0;
    const totalRevenue = historicalPrices.reduce((s, p) => s + p.price * p.demand, 0);

    return {
      id: product.id || index + 1,
      name: product.name,
      imageLink: product.imageLink,
      mcCategory: product.mcCategory,
      category: categorizeProduct(product.name, product.brand),
      currentPrice: latestMonth.price,
      historicalPrices,
      elasticity,
      optimumPrice: Math.round(revenueOpt.optimumPrice * 100) / 100,
      currentRevenue: Math.round(currentRevenue),
      optimumRevenue: Math.round(revenueOpt.optimumRevenue),
      potentialGrowth: Math.round(potentialGrowth * 10) / 10,
      totalRevenue: Math.round(totalRevenue),
      currentDemand: latestMonth.demand,
    };
  });
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function quantile(sortedArr, q) {
  const pos = (sortedArr.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedArr[base + 1] !== undefined) return sortedArr[base] + rest * (sortedArr[base + 1] - sortedArr[base]);
  return sortedArr[base];
}

function monthDiff(a, b) {
  return (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth());
}

function nonEmpty(val) {
  return typeof val === 'string' && val.trim().length > 0 ? val.trim() : null;
}

function categorizeProduct(name, brand) {
  const n = (name || '').toLowerCase();
  const b = (brand || '').toLowerCase();
  if (n.includes('shoe') || n.includes('sneaker') || b.includes('nike') || b.includes('adidas') || b.includes('skechers')) return 'Footwear';
  if (n.includes('shirt') || n.includes('jacket') || n.includes('pant') || n.includes('hoodie')) return 'Apparel';
  if (n.includes('watch') || n.includes('accessory') || n.includes('belt')) return 'Accessories';
  if (n.includes('book')) return 'Books & Media';
  return 'General Products';
}
